import React, { useMemo, useState } from "react";
import { isLate, isAbsent, parseLateMinutes } from "./StaffAnalyticsTab";
import TabKpiRow from "./TabKpiRow";
import DisciplineComplianceSection from "./DisciplineComplianceSection";

const TOP_N = 20;
const REPEAT_THRESHOLD = 10;

const computeDisciplineKpis = (allStats, prevStats) => {
  const totalLate   = allStats.reduce((s, x) => s + x.lateCount, 0);
  const repeatIds   = new Set(prevStats.map((s) => s.userId));
  const repeatCount = allStats.filter((s) => repeatIds.has(s.userId)).length;
  const allLateMin  = allStats.reduce((s, x) => s + x.totalLateMinutes, 0);
  const allLateCnt  = allStats.reduce((s, x) => s + x.lateCount, 0);
  const avgLate     = allLateCnt > 0 ? Math.round(allLateMin / allLateCnt) : 0;
  const worst       = allStats[0];
  return [
    { label: "Total Late Days",  value: totalLate,                          mod: totalLate > 20 ? "red" : totalLate > 10 ? "orange" : "blue" },
    { label: "Repeat Offenders", value: repeatCount,                        mod: repeatCount > 3 ? "red" : repeatCount > 0 ? "orange" : "green" },
    { label: "Avg Late (min)",   value: avgLate > 0 ? `${avgLate}m` : "—", mod: avgLate > 30 ? "red" : avgLate > 10 ? "orange" : "green" },
    { label: "Worst Offender",   value: worst ? worst.name : "None",        mod: "red", sub: worst ? `${worst.lateCount} late days` : undefined },
  ];
};

const buildStats = (shifts) => {
  const map = {};
  shifts.forEach(({ userId, name, accountType, gymName, att, date }) => {
    if (!map[userId]) {
      map[userId] = { userId, name, accountType, gymName, lateCount: 0, absentCount: 0, totalLateMinutes: 0, timeline: [] };
    }
    const s = map[userId];
    if (isLate(att))   { s.lateCount++; s.totalLateMinutes += parseLateMinutes(att.shift_difference); }
    if (isAbsent(att)) { s.absentCount++; }
    s.timeline.push({ date, att });
  });
  return Object.values(map)
    .filter((s) => s.lateCount > 0 || s.absentCount > 0)
    .sort((a, b) => {
      if (b.lateCount !== a.lateCount) return b.lateCount - a.lateCount;
      const avgA = a.lateCount ? a.totalLateMinutes / a.lateCount : 0;
      const avgB = b.lateCount ? b.totalLateMinutes / b.lateCount : 0;
      return avgB - avgA;
    });
};

const AnalyticsDisciplineLeaderboard = ({ allShifts = [], prevShifts = [], dateFrom, dateTo }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [search, setSearch] = useState("");

  const allStats  = useMemo(() => buildStats(allShifts).slice(0, TOP_N), [allShifts]);
  const prevStats = useMemo(() => buildStats(prevShifts).slice(0, REPEAT_THRESHOLD), [prevShifts]);

  const repeatIds = useMemo(() => new Set(prevStats.map((s) => s.userId)), [prevStats]);

  const filtered = useMemo(() => {
    if (!search.trim()) return allStats;
    const q = search.toLowerCase();
    return allStats.filter((s) => s.name.toLowerCase().includes(q) || s.gymName.toLowerCase().includes(q));
  }, [allStats, search]);

  const maxLate = allStats[0]?.lateCount || 1;
  const kpiCards = useMemo(() => computeDisciplineKpis(allStats, prevStats), [allStats, prevStats]);

  if (allStats.length === 0) {
    return <div className="sta-empty">No late or absent records in this period.</div>;
  }

  return (
    <div>
      <TabKpiRow cards={kpiCards} />
      <div className="sta-discipline-controls">
        <input
          type="text"
          className="sta-search"
          placeholder="Search by name or gym…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <span style={{ fontSize: 12, color: "#888" }}>
            {filtered.length} of {allStats.length} staff
          </span>
        )}
        {repeatIds.size > 0 && (
          <span style={{ fontSize: 12, color: "#888" }}>
            <span className="sta-repeat-badge">REPEAT</span> = also in top-{REPEAT_THRESHOLD} last period
          </span>
        )}
      </div>

      <div className="sta-leaderboard">
        <table className="sta-leaderboard__table">
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Gym</th>
              <th>Role</th>
              <th>Late Days</th>
              <th>Absent Days</th>
              <th>Avg Late</th>
              <th style={{ minWidth: 100 }}>Frequency</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s, i) => {
              const avg        = s.lateCount ? Math.round(s.totalLateMinutes / s.lateCount) : 0;
              const barPct     = Math.round((s.lateCount / maxLate) * 100);
              const isExpanded = expandedId === s.userId;
              const isRepeat   = repeatIds.has(s.userId);
              const rankClass  = i < 3 ? ` sta-rank--${i + 1}` : "";

              return (
                <React.Fragment key={s.userId}>
                  <tr
                    onClick={() => setExpandedId(isExpanded ? null : s.userId)}
                    style={{ cursor: "pointer" }}
                    title="Click to expand day-by-day timeline"
                  >
                    <td><span className={`sta-rank${rankClass}`}>{i + 1}</span></td>
                    <td style={{ fontWeight: 600 }}>
                      {s.name}
                      {isRepeat && <span className="sta-repeat-badge">REPEAT</span>}
                    </td>
                    <td style={{ fontSize: 12, color: "#666" }}>{s.gymName}</td>
                    <td style={{ fontSize: 12 }}>{s.accountType === "trainer" ? "Trainer" : "Sales Exec"}</td>
                    <td>
                      <span style={{ color: s.lateCount > 5 ? "#dc3545" : s.lateCount > 2 ? "#fd7e14" : "#555", fontWeight: 600 }}>
                        {s.lateCount}
                      </span>
                    </td>
                    <td>
                      <span style={{ color: s.absentCount > 3 ? "#dc3545" : "#555", fontWeight: 600 }}>
                        {s.absentCount}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: "#666" }}>{avg > 0 ? `${avg} min` : "—"}</td>
                    <td>
                      <div className="sta-bar-wrap">
                        <div className="sta-bar sta-bar--late" style={{ width: `${barPct}%`, maxWidth: 80 }} />
                        <span style={{ fontSize: 11, color: "#888" }}>{s.lateCount}d</span>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={8} style={{ background: "#f8f9fa", padding: "10px 16px" }}>
                        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: "#555" }}>
                          {s.name} — day-by-day timeline
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {s.timeline
                            .sort((a, b) => (a.date < b.date ? -1 : 1))
                            .map(({ date, att }) => {
                              const late   = isLate(att);
                              const absent = isAbsent(att);
                              const color  = absent ? "#dc3545" : late ? "#fd7e14" : "#28a745";
                              const label  = absent ? "A" : late ? "L" : "✓";
                              return (
                                <div key={date} title={`${date}: ${absent ? "Absent" : late ? `Late +${att.shift_difference||"?"}` : att.status||"Present"}`}
                                  style={{ width:32, height:32, borderRadius:4, background:color, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:10, cursor:"help" }}>
                                  <span style={{ fontWeight:700 }}>{label}</span>
                                  <span style={{ fontSize:8 }}>{date.slice(8)}</span>
                                </div>
                              );
                            })}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Leave Compliance Section ─────────────────── */}
      <DisciplineComplianceSection allShifts={allShifts} dateFrom={dateFrom} dateTo={dateTo} />
    </div>
  );
};

export default AnalyticsDisciplineLeaderboard;
